import { useState, useEffect } from 'react'
import VoiceTextarea from '@/components/VoiceTextarea'
import { Table, Button, Modal, Form, Input, Select, Space, Tag, Popconfirm, message, InputNumber, Row, Col, Divider, Upload, DatePicker, Card, Alert, List, theme } from 'antd'
import { PlusOutlined, EditOutlined, DeleteOutlined, CarOutlined, DownloadOutlined, UploadOutlined, FileAddOutlined } from '@ant-design/icons'
import { AutoRefreshBadge } from '@/components/AutoRefreshBadge'
import type { ColumnsType } from 'antd/es/table'
import type { UploadFile } from 'antd/es/upload/interface'
import dayjs from 'dayjs'
import { useCourtesyCars, useCreateCourtesyCar, useUpdateCourtesyCar, useDeleteCourtesyCar, useUploadContratto } from '@/hooks/useCourtesyCars'
import { useVehicles } from '@/hooks/useVehicles'
import { usePageTitle } from '@/contexts/PageTitleContext'
import { usePermission } from '@/hooks/usePermission'
import { formatEuro } from '@/utils/formatters'
import type { CourtesyCar } from '@/types'

const CourtesyCarsPage = () => {
  const { token } = theme.useToken()
  const { setTitle } = usePageTitle()
  const canCreate = usePermission('courtesy_cars.create')
  const canEdit   = usePermission('courtesy_cars.edit')
  const canDelete = usePermission('courtesy_cars.delete')
  const [pageSize, setPageSize] = useState(10)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingCourtesyCar, setEditingCourtesyCar] = useState<CourtesyCar | null>(null)
  const [form] = Form.useForm()
  const [fileList, setFileList] = useState<UploadFile[]>([])
  const [isUploading, setIsUploading] = useState(false)
  const [prefilledVehicleId, setPrefilledVehicleId] = useState<number | undefined>(undefined)

  useEffect(() => {
    setTitle('Gestione Auto di Cortesia')
  }, [setTitle])

  const { data, isLoading, refetch, dataUpdatedAt } = useCourtesyCars(1, 200)
  const { data: vehiclesData } = useVehicles(1, 10000)
  const createMutation = useCreateCourtesyCar()
  const updateMutation = useUpdateCourtesyCar()
  const deleteMutation = useDeleteCourtesyCar()
  const uploadMutation = useUploadContratto(editingCourtesyCar?.id || 0)

  // Veicoli flaggati come ADC
  const courtesyVehicles = vehiclesData?.items?.filter(v => v.courtesy_car === true) || []

  // ADC senza contratto = record in courtesy_cars con contratto_tipo null
  const pendingVehicles = (data?.items ?? []).filter(cc => !cc.contratto_tipo)


  const handleCreate = (vehicleId?: number) => {
    setEditingCourtesyCar(null)
    form.resetFields()
    setFileList([])
    setPrefilledVehicleId(vehicleId)
    if (vehicleId !== undefined) {
      form.setFieldsValue({ vehicle_id: vehicleId })
    }
    setIsModalOpen(true)
  }

  const handleEdit = (record: CourtesyCar) => {
    setEditingCourtesyCar(record)
    form.setFieldsValue(record)
    // Se ha un file, mostra nel upload
    if (record.contratto_firmato) {
      setFileList([
        {
          uid: '-1',
          name: 'contratto.pdf',
          status: 'done',
          url: `/api/v1/courtesy-cars/${record.id}/contratto/download`,
        },
      ])
    } else {
      setFileList([])
    }
    setIsModalOpen(true)
  }

  const handleDelete = async (id: number) => {
    await deleteMutation.mutateAsync(id)
    refetch()
  }

  const handleSubmit = async (values: any) => {
    try {
      setIsUploading(true)
      
      if (editingCourtesyCar) {
        await updateMutation.mutateAsync({
          id: editingCourtesyCar.id,
          data: values,
        })
      } else {
        await createMutation.mutateAsync(values)
      }

      // Upload del file PDF se presente e nuovo
      if (fileList.length > 0 && fileList[0].originFileObj) {
        const newFile = fileList[0].originFileObj as File
        
        // Attendi 500ms per assicurarti che il record sia stato salvato
        await new Promise(resolve => setTimeout(resolve, 500))
        
        // Carica il file
        await uploadMutation.mutateAsync(newFile)
        message.success('Contratto caricato con successo!')
      }

      setIsModalOpen(false)
      form.resetFields()
      setFileList([])
      setPrefilledVehicleId(undefined)
      refetch()
    } catch (error: any) {
      console.error('Errore:', error)
      message.error(error?.message || 'Errore durante il salvataggio')
    } finally {
      setIsUploading(false)
    }
  }

  // Mapping stato con colori
  const getStatusTag = (status: string) => {
    const statusColors: Record<string, string> = {
      DISPONIBILE: 'green',
      ASSEGNATA: 'blue',
      MANUTENZIONE: 'orange',
      FUORI_SERVIZIO: 'red',
    }
    const statusLabels: Record<string, string> = {
      DISPONIBILE: '✅ Disponibile',
      ASSEGNATA: '🏍️ Assegnata',
      MANUTENZIONE: '🔧 Manutenzione',
      FUORI_SERVIZIO: '🚫 Fuori Servizio',
    }
    return (
      <Tag color={statusColors[status] || 'default'}>
        {statusLabels[status] || status}
      </Tag>
    )
  }

  // Ottieni nome veicolo dal vehicle_id
  const getVehicleName = (vehicleId: number) => {
    const vehicle = courtesyVehicles.find(v => v.id === vehicleId)
    if (!vehicle) return '-'
    return `${vehicle.targa} - ${vehicle.marca} ${vehicle.modello}`
  }

  const columns: ColumnsType<CourtesyCar> = [
    {
      title: 'Veicolo',
      key: 'vehicle',
      render: (_, record) => (
        <Space>
          <CarOutlined />
          <strong>{getVehicleName(record.vehicle_id)}</strong>
        </Space>
      ),
    },
    {
      title: 'Tipo Contratto',
      dataIndex: 'contratto_tipo',
      key: 'contratto_tipo',
      render: (tipo) => (
        <Tag color={tipo === 'LEASING' ? 'blue' : tipo === 'AFFITTO' ? 'green' : 'default'}>
          {tipo?.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Fornitore',
      dataIndex: 'fornitore_contratto',
      key: 'fornitore_contratto',
      render: (fornitore) => fornitore || '-',
    },
    {
      title: 'Stato',
      key: 'stato',
      render: (_, record) => getStatusTag(record.stato),
    },
    {
      title: 'Canone Mensile',
      dataIndex: 'canone_mensile',
      key: 'canone_mensile',
      render: (canone) => canone ? formatEuro(parseFloat(canone)) : '-',
    },
    {
      title: 'Scadenza',
      dataIndex: 'data_scadenza_contratto',
      key: 'data_scadenza_contratto',
      render: (date) => date ? dayjs(date).format('DD/MM/YYYY') : '-',
    },
    {
      title: 'Contratto',
      dataIndex: 'contratto_firmato',
      key: 'contratto_firmato',
      render: (contratto, record) => (
        contratto ? (
          <Button
            type="link"
            icon={<DownloadOutlined />}
            href={`/api/v1/courtesy-cars/${record.id}/contratto/download`}
            target="_blank"
          >
            Scarica
          </Button>
        ) : (
          <span style={{ color: token.colorTextTertiary }}>Nessun file</span>
        )
      ),
    },
    ...(canEdit || canDelete ? [{
      title: 'Azioni',
      key: 'actions',
      render: (_: any, record: CourtesyCar) => (
        <Space>
          {canEdit && (
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            >
              Modifica
            </Button>
          )}
          {canDelete && (
            <Popconfirm
              title="Sei sicuro di voler eliminare questa auto di cortesia?"
              onConfirm={() => handleDelete(record.id)}
              okText="Sì"
              cancelText="No"
            >
              <Button type="link" danger icon={<DeleteOutlined />}>
                Elimina
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    }] : []),
  ]

  return (
    <div>
      <div style={{ marginBottom: 24, display: 'flex', gap: 12, justifyContent: 'flex-end', alignItems: 'center' }}>
        <AutoRefreshBadge updatedAt={dataUpdatedAt || null} />
        {canCreate && (
          <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
            Nuovo Contratto
          </Button>
        )}
      </div>

      {courtesyVehicles.length === 0 ? (
        <Card style={{ textAlign: 'center', padding: '40px' }}>
          <div style={{ marginBottom: '16px', fontSize: '32px' }}>🚗</div>
          <div style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '8px' }}>
            Nessuna auto di cortesia disponibile
          </div>
          <div style={{ fontSize: '14px', color: token.colorTextTertiary }}>
            Prima flagga un veicolo come "Auto di Cortesia" nella sezione Gestione Veicoli
          </div>
        </Card>
      ) : (
        <>
          {/* Veicoli senza contratto registrato */}
          {pendingVehicles.length > 0 && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message={
                <strong>{pendingVehicles.length} veicolo{pendingVehicles.length > 1 ? 'i' : ''} senza contratto registrato</strong>
              }
              description={
                <List
                  size="small"
                  style={{ marginTop: 8 }}
                  dataSource={pendingVehicles}
                  renderItem={cc => {
                    const veh = courtesyVehicles.find(v => v.id === cc.vehicle_id)
                    return (
                      <List.Item
                        style={{ padding: '6px 0' }}
                        actions={[
                          canCreate && (
                            <Button
                              key="registra"
                              type="primary"
                              size="small"
                              icon={<FileAddOutlined />}
                              onClick={() => handleEdit(cc)}
                            >
                              Registra contratto
                            </Button>
                          ),
                        ].filter(Boolean)}
                      >
                        <Space>
                          <CarOutlined />
                          <strong>{veh?.targa ?? `#${cc.vehicle_id}`}</strong>
                          <span style={{ color: token.colorTextSecondary }}>{veh?.marca} {veh?.modello}{veh?.anno ? ` (${veh.anno})` : ''}</span>
                        </Space>
                      </List.Item>
                    )
                  }}
                />
              }
            />
          )}

          <Table
            columns={columns}
            dataSource={data?.items || []}
            rowKey="id"
            loading={isLoading}
            pagination={{
              pageSize,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50', '100'],
              onShowSizeChange: (_: number, size: number) => setPageSize(size),
              showTotal: (total) => `Totale ${total} contratti`,
            }}
          />
        </>
      )}

      {/* MODAL */}
      <Modal
        title={editingCourtesyCar ? 'Modifica Auto di Cortesia' : 'Nuovo Contratto Auto di Cortesia'}
        open={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false)
          form.resetFields()
          setFileList([])
          setPrefilledVehicleId(undefined)
        }}
        onOk={() => form.submit()}
        width={800}
        confirmLoading={isUploading || createMutation.isPending || updateMutation.isPending}
        okButtonProps={{ disabled: isUploading || createMutation.isPending || updateMutation.isPending }}
      >
        {/* Intestazione veicolo */}
        {editingCourtesyCar && (
          <Card style={{ marginBottom: '16px', backgroundColor: token.colorFillAlter }}>
            <Row gutter={16}>
              <Col span={12}>
                <div style={{ fontSize: '12px', color: token.colorTextTertiary }}>VEICOLO</div>
                <div style={{ fontSize: '14px', fontWeight: 'bold', marginTop: '4px' }}>
                  {getVehicleName(editingCourtesyCar.vehicle_id)}
                </div>
              </Col>
              <Col span={12}>
                <div style={{ fontSize: '12px', color: token.colorTextTertiary }}>STATO ATTUALE</div>
                <div style={{ marginTop: '4px' }}>
                  {getStatusTag(editingCourtesyCar.stato)}
                </div>
              </Col>
            </Row>
          </Card>
        )}

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          {!editingCourtesyCar && (
            <Form.Item
              name="vehicle_id"
              label="Veicolo"
              rules={[{ required: true, message: 'Seleziona un veicolo' }]}
            >
              <Select
                placeholder="Seleziona auto di cortesia"
                disabled={prefilledVehicleId !== undefined}
                options={courtesyVehicles
                  .filter(v => !data?.items?.some(cc => cc.vehicle_id === v.id))
                  .map(v => ({
                    value: v.id,
                    label: `${v.targa} - ${v.marca} ${v.modello} (${v.anno})`,
                  }))}
              />
            </Form.Item>
          )}

          <Divider orientation="left">Dati Contrattuali</Divider>

          <Form.Item
            name="contratto_tipo"
            label="Tipo di Contratto"
            rules={[{ required: true, message: 'Seleziona il tipo' }]}
          >
            <Select
              placeholder="Leasing / Affitto / Proprietà"
              options={[
                { label: '📋 Leasing', value: 'LEASING' },
                { label: '🏠 Affitto', value: 'AFFITTO' },
                { label: '🔑 Proprietà Tiber Car', value: 'PROPRIETA' },
              ]}
            />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="fornitore_contratto"
                label="Fornitore/Società"
              >
                <Input placeholder="Leasing company, etc." maxLength={200} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="stato"
                label="Stato Auto"
              >
                <Select
                  placeholder="Stato"
                  options={[
                    { label: '✅ Disponibile', value: 'DISPONIBILE' },
                    { label: '🏍️ Assegnata', value: 'ASSEGNATA' },
                    { label: '🔧 Manutenzione', value: 'MANUTENZIONE' },
                    { label: '🚫 Fuori Servizio', value: 'FUORI_SERVIZIO' },
                  ]}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="data_inizio_contratto"
                label="Data Inizio Contratto"
              >
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="data_scadenza_contratto"
                label="Data Scadenza Contratto"
              >
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="canone_mensile"
                label="Canone Mensile (€)"
              >
                <InputNumber min={0} step={0.01} style={{ width: '100%' }} placeholder="1.000,00" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="km_inclusi_anno"
                label="KM Inclusi per Anno"
              >
                <InputNumber min={0} style={{ width: '100%' }} placeholder="15.000" />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">Documenti</Divider>

          <Form.Item
            name="contratto_firmato"
            label="Contratto Firmato (PDF)"
          >
            <Upload
              accept=".pdf"
              maxCount={1}
              fileList={fileList}
              onChange={({ fileList: newFileList }) => setFileList(newFileList)}
            >
              <Button icon={<UploadOutlined />}>Carica PDF</Button>
            </Upload>
          </Form.Item>

          <Divider orientation="left">Note</Divider>

          <Form.Item
            name="note"
            label="Note"
          >
            <VoiceTextarea rows={3} minHeight={90} maxHeight={200} placeholder="Note aggiuntive..." label="Note" compact />
          </Form.Item>

          {/* Campi di sistema (visualizzazione sola lettura) */}
          {editingCourtesyCar && (
            <>
              <Divider orientation="left">Informazioni di Sistema</Divider>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item label="Creato il">
                    <div>{dayjs(editingCourtesyCar.created_at).format('DD/MM/YYYY HH:mm')}</div>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Modificato il">
                    <div>{dayjs(editingCourtesyCar.updated_at).format('DD/MM/YYYY HH:mm')}</div>
                  </Form.Item>
                </Col>
              </Row>
            </>
          )}
        </Form>
      </Modal>
    </div>
  )
}

export default CourtesyCarsPage
